var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { LocalStorageCache } from './extensions/LocalStorageCache';
import OpenIdConnectClient from './extensions/OpenIdConnectClient';
import { openPopup } from './utils';
const DEFAULT_STORAGE_NAMESPACE_PREFIX = 'profiles-spa-client';
// in milliseconds (60 secs)
const DEFAULT_EXP_OFFSET = 60000;
export class ProfilesClient {
    constructor(options) {
        this.subscribers = [];
        // Configuration options
        this.issuer = '';
        this.clientId = '';
        this.audience = '';
        this.redirectUri = '';
        this.logoutUri = '';
        this.jwksURI = '';
        this.defaultScope = 'openid';
        this.namespace = '';
        this.onlyInternalFlow = false;
        this.crossTabChannel = window.BroadcastChannel ? new BroadcastChannel(DEFAULT_STORAGE_NAMESPACE_PREFIX) : undefined;
        this.useCache = true;
        this.redirectToLogoutUriAfterCrossTabLogout = true;
        this.initOptions(options);
        this.initClient();
        if (options.autoRenew) {
            this.start().catch(() => {
                console.error('Profiles client: autostart failed.');
            });
        }
        if (this.crossTabChannel) {
            this.crossTabChannel.onmessage = (event) => {
                if (event.data === "logout") {
                    this.logout(true, false, this.redirectToLogoutUriAfterCrossTabLogout);
                }
            };
        }
    }
    loginWithRedirect() {
        return this.oidcClient.loginWithRedirect();
    }
    loginWithPopup() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.oidcClient.loginWithPopup({}, {
                popup: openPopup('')
            });
            return this.publishAuthState();
        });
    }
    handleRedirect() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.oidcClient.handleRedirectCallback();
            return this.publishAuthState();
        });
    }
    // at the moment federated logout is not supported
    logout(localOnly = true, logoutFromAllTabs = true, redirectToLogoutUri = true) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.oidcClient.logout({ localOnly });
            if (logoutFromAllTabs && this.crossTabChannel) {
                this.crossTabChannel.postMessage('logout');
                this.crossTabChannel.close();
            }
            if (this.logoutUri && redirectToLogoutUri) {
                window.location.href = this.logoutUri;
            }
            return this.publishAuthState();
        });
    }
    loginSilently() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.silentRenew(true);
            return this.publishAuthState();
        });
    }
    isLoginRedirect(location = window.location.href) {
        return this.isRedirectUri(location)
            && (this.containsCode(location) || this.containsError(location));
    }
    isAuthenticated() {
        return this.oidcClient.isAuthenticated();
    }
    getUserInfo() {
        return this.oidcClient.getUser();
    }
    // At the moment there is not scoping for access tokens
    getAccessToken() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.oidcClient.getAccessToken(this.defaultScope, this.audience, this.clientId);
        });
    }
    getTokensSilently() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.oidcClient.getTokenSilently({ scope: this.defaultScope, audience: this.audience, detailedResponse: true });
        });
    }
    // This could be performance leak
    getAuthState() {
        return __awaiter(this, void 0, void 0, function* () {
            const userInfo = yield this.getUserInfo();
            const { access_token, id_token } = yield this.oidcClient.getCacheEntry(this.defaultScope, this.audience, this.clientId);
            const idToken = yield this.oidcClient.getIdTokenClaims();
            return {
                isAuthenticated: !!userInfo,
                userInfo,
                accessToken: access_token,
                idToken: id_token,
                iat: (idToken === null || idToken === void 0 ? void 0 : idToken.iat) ? idToken.iat * 1000 : undefined
            };
        });
    }
    subscribe(callback) {
        this.subscribers.push(callback);
    }
    unsubscribe(callback) {
        this.subscribers = this.subscribers.filter(cb => cb !== callback);
    }
    start() {
        return __awaiter(this, void 0, void 0, function* () {
            //cleanup
            if (this.syncTimeout) {
                this.stop();
            }
            const isAuthenticated = yield this.isAuthenticated();
            try {
                if (!isAuthenticated) {
                    yield this.silentRenew();
                }
                yield this.publishAuthState();
            }
            catch (_a) {
                return this.stop();
            }
            this.sync();
        });
    }
    stop() {
        if (this.syncTimeout) {
            clearTimeout(this.syncTimeout);
        }
        this.syncTimeout = undefined;
    }
    silentRenew(ignoreCache = false) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.oidcClient.getTokenSilently({ ignoreCache });
            return this.getAuthState();
        });
    }
    sync() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                // in epoch time
                const now = (new Date()).getTime();
                const claims = yield this.oidcClient.getIdTokenClaims();
                const timeout = claims.exp * 1000 - now - DEFAULT_EXP_OFFSET;
                yield this.publishAuthState();
                this.syncTimeout = setTimeout(() => __awaiter(this, void 0, void 0, function* () {
                    yield this.sync();
                }), timeout > 0 ? timeout : 0);
            }
            catch (_a) {
                this.stop();
            }
        });
    }
    initOptions(options) {
        if (!options.issuer) {
            throw new Error('Issuer required!');
        }
        const issuerUrl = new URL(options.issuer);
        if (!issuerUrl.origin) {
            throw new Error('Issuer invalid. URL expected!');
        }
        this.issuer = options.issuer;
        if (!options.clientId) {
            throw new Error('ClientId required!');
        }
        this.clientId = options.clientId;
        // `audience` same as `clientId`
        this.audience = options.clientId;
        if (options.useCache !== undefined) {
            this.useCache = options.useCache;
        }
        if (!options.redirectUri) {
            throw new Error('Redirect URI required!');
        }
        // add trailing `/`, if not there
        // also `profiles - doorkeeper` adds trailing `/` to issuer JWT claim
        this.redirectUri = options.redirectUri.substr(-1) === '/' ? options.redirectUri : `${options.redirectUri}/`;
        if (options.logoutUri) {
            this.logoutUri = options.logoutUri;
        }
        if (options.redirectToLogoutUriAfterCrossTabLogout !== undefined) {
            this.redirectToLogoutUriAfterCrossTabLogout = options.redirectToLogoutUriAfterCrossTabLogout;
        }
        this.onlyInternalFlow = !!options.onlyInternalFlow;
        this.namespace = options.namespace || DEFAULT_STORAGE_NAMESPACE_PREFIX;
        this.jwksURI = `${this.issuer}/oauth/discovery/keys`;
    }
    initClient() {
        this.oidcClient = new OpenIdConnectClient({
            domain: this.issuer,
            issuer: this.issuer,
            client_id: this.clientId,
            audience: this.audience,
            redirect_uri: this.redirectUri,
            pkce: true,
            // this is important: Auth0 mitigated ITP in browser by enhancing refresh tokens
            // you need sophisticated processing of refresh tokens
            useRefreshTokens: false,
            legacySameSiteCookie: false,
            auth0Client: null,
            useCookiesForTransactions: false,
            advancedOptions: {
                defaultScope: this.defaultScope
            },
            cache: this.useCache ? new LocalStorageCache(this.namespace) : undefined
        }, this.namespace, this.onlyInternalFlow);
    }
    isRedirectUri(location) {
        return location.indexOf(this.redirectUri) !== -1;
    }
    containsCode(location) {
        return location.indexOf('?code=') !== -1;
    }
    containsError(location) {
        return location.indexOf('error=') !== -1;
    }
    publishAuthState() {
        return __awaiter(this, void 0, void 0, function* () {
            const authState = yield this.getAuthState();
            this.subscribers.forEach(callback => callback(authState));
            return authState;
        });
    }
}
//# sourceMappingURL=ProfilesClient.js.map